import React from "react";
import Section from "../MoveAction/RevisionPreview/Section";
import Step from "../MoveAction/RevisionPreview/Step/Step";
import { getAllNodeIndices } from "../utils/previewUtils";
import {
  getProcedureWideStepDisplayOrdinal,
  getSectionDisplayOrdinal,
  parseAuditSectionNumberFromLabel,
  parseReadOnlyCheckboxTitleMatch,
} from "../utils/sectionDisplayOrdinal";

/** Procedure tree_node_id first — matches audit preview_tree_node_id; section_tree_node_id is entity id. */
const getSectionId = (section) =>
  section?.tree_node_id ||
  section?.version_details?.version_content?.section_tree_node_id ||
  section?.detail?.section_tree_node_id ||
  section?.section_tree_node_id ||
  section?.section_id_ref ||
  section?.version_id_ref;

const getSectionCheckIds = (section) =>
  [
    section?.tree_node_id,
    section?.detail?.procedure_tree_ref,
    section?.version_details?.version_content?.procedure_tree_ref,
    section?.version_details?.version_content?.section_tree_node_id,
    section?.detail?.section_tree_node_id,
    section?.section_tree_node_id,
    section?.section_id_ref,
  ].filter(Boolean);

const isSectionSelected = (section, selectedNodes) =>
  getSectionCheckIds(section).some((id) => selectedNodes[id]);

/** Procedure tree_node_id first — matches audit preview_tree_node_id for step moves. */
const getStepId = (step) =>
  step?.tree_node_id ||
  step?.version_details?.version_content?.step_tree_node_id ||
  step?.detail?.step_tree_node_id ||
  step?.step_tree_node_id ||
  step?.step_id_ref ||
  step?.version_id_ref;

const getStepCheckIds = (step) =>
  [
    step?.tree_node_id,
    step?.version_details?.version_content?.step_tree_node_id,
    step?.detail?.step_tree_node_id,
    step?.step_tree_node_id,
    step?.step_id_ref,
  ].filter(Boolean);

const isStepSelected = (step, selectedNodes) =>
  getStepCheckIds(step).some((id) => selectedNodes[id]);

const getSubstepId = (substep) =>
  substep?.version_details?.version_content?.sub_step_tree_node_id ||
  substep?.sub_step_tree_node_id ||
  substep?.substep_tree_node_id ||
  substep?.substep_id_ref ||
  substep?.tree_node_id ||
  substep?.version_id_ref;

const getChildNodeId = (child) =>
  child?.tree_node_id ||
  child?.detail?.procedure_tree_ref ||
  child?.version_details?.version_content?.procedure_tree_ref ||
  child?.version_details?.version_content?.element_id ||
  child?.version_details?.version_content?.canvas_tree_node_id ||
  child?.version_details?.version_content?.canva_tree_node_id ||
  child?.version_details?.version_content?.rich_text_node_id ||
  child?.version_details?.version_content?.rich_text_tree_node_id ||
  child?.version_details?.version_content?.video_tree_node_id ||
  child?.version_details?.version_content?.attachment_tree_node_id ||
  child?.version_details?.version_content?.preview_tree_node_id ||
  child?.detail?.element_id ||
  child?.detail?.canvas_tree_node_id ||
  child?.detail?.canva_tree_node_id ||
  child?.detail?.rich_text_node_id ||
  child?.detail?.rich_text_tree_node_id ||
  child?.detail?.video_tree_node_id ||
  child?.detail?.attachment_tree_node_id ||
  child?.detail?.preview_tree_node_id;

const filterContentDataToFocusNode = (contentData, focusNodeId) => {
  if (!focusNodeId) return contentData;

  for (const section of contentData || []) {
    const sectionId = getSectionId(section);
    if (sectionId && sectionId === focusNodeId) {
      return [{ ...section }];
    }

    for (const step of section?.steps || []) {
      const stepId = getStepId(step);
      if (stepId && stepId === focusNodeId) {
        return [
          {
            ...section,
            hideSectionHeader: true,
            steps: [{ ...step }],
          },
        ];
      }

      // Substep children before step-level children: same attachment id can exist under both; prefer substep path (copy into substep)
      for (const substep of step?.substeps || []) {
        const subKids = substep?.children || [];
        for (let ci = subKids.length - 1; ci >= 0; ci--) {
          const child = subKids[ci];
          const childId = getChildNodeId(child);
          if (childId && childId === focusNodeId) {
            return [
              {
                ...section,
                hideSectionHeader: true,
                steps: [
                  {
                    ...step,
                    children: [],
                    substeps: [
                      {
                        ...substep,
                        children: [{ ...child }],
                      },
                    ],
                  },
                ],
              },
            ];
          }
        }
      }

      // Step-level children (canvas/rich-text/video/attachment/etc.) — last match wins for duplicate tree_node_id (e.g. CANVA_COPIED)
      const stepKids = step?.children || [];
      for (let ci = stepKids.length - 1; ci >= 0; ci--) {
        const child = stepKids[ci];
        const childId = getChildNodeId(child);
        if (childId && childId === focusNodeId) {
          return [
            {
              ...section,
              hideSectionHeader: true,
              steps: [
                {
                  ...step,
                  children: [{ ...child }],
                  substeps: [],
                },
              ],
            },
          ];
        }
      }

      for (const substep of step?.substeps || []) {
        const substepId = getSubstepId(substep);
        if (substepId && substepId === focusNodeId) {
          return [
            {
              ...section,
              hideSectionHeader: true,
              steps: [
                {
                  ...step,
                  children: [],
                  substeps: [{ ...substep }],
                },
              ],
            },
          ];
        }
      }
    }
  }

  // If we can't find it, fall back to full content rather than rendering nothing.
  return contentData;
};

/**
 * Reusable component for rendering preview content (sections, steps, and nodes)
 * @param {Array} contentData - Processed content data
 * @param {Boolean} showCheckboxes - Whether to show checkboxes for node selection
 * @param {Object} selectedNodes - Object tracking which nodes are selected
 * @param {Function} onNodeToggle - Handler for checkbox toggle
 * @param {Boolean} disabled - Whether checkboxes should be disabled
 * @param {String} focusNodeId - If provided, only render this node's subtree
 * @returns {JSX.Element} Rendered preview content
 */
/** Ids that may align with audit version_content.preview_tree_node_id / procedure_tree_ref. */
const getStepFocusMatchIds = (step) => {
  const d = step?.detail || {};
  const vc = step?.version_details?.version_content || {};
  const base = getStepCheckIds(step);
  const extra = [
    d.procedure_tree_ref,
    vc.procedure_tree_ref,
    d.element_id,
    vc.element_id,
    d.preview_tree_node_id,
    vc.preview_tree_node_id,
  ].filter(Boolean);
  return [...base, ...extra];
};

/** When set with auditStepOrdinalOverride, only that step row gets the ordinal (Review route full tree). */
const stepMatchesAuditFocus = (step, focusId) => {
  if (focusId == null || focusId === "") return false;
  return getStepFocusMatchIds(step).some(
    (id) => id === focusId || String(id) === String(focusId),
  );
};

const getSubstepFocusMatchIds = (substep) => {
  const d = substep?.detail || {};
  const vc = substep?.version_details?.version_content || {};
  return [
    substep?.tree_node_id,
    substep?.sub_step_tree_node_id,
    substep?.substep_tree_node_id,
    substep?.substep_id_ref,
    vc.sub_step_tree_node_id,
    vc.substep_tree_node_id,
    d.sub_step_tree_node_id,
    d.substep_tree_node_id,
    d.procedure_tree_ref,
    vc.procedure_tree_ref,
    d.preview_tree_node_id,
    vc.preview_tree_node_id,
  ].filter(Boolean);
};

/** Audit focus id may be sub_step_tree_node_id while step row ids differ — match parent step via child substep. */
const stepOrSubstepMatchesAuditFocus = (step, focusId) => {
  if (stepMatchesAuditFocus(step, focusId)) return true;
  for (const ss of step?.substeps || []) {
    if (
      getSubstepFocusMatchIds(ss).some(
        (id) => id === focusId || String(id) === String(focusId),
      )
    )
      return true;
  }
  return false;
};

const treeHasAuditStepFocusHit = (sections, focusId) => {
  if (focusId == null || focusId === "") return false;
  for (const sec of sections || []) {
    for (const st of sec?.steps || []) {
      if (stepOrSubstepMatchesAuditFocus(st, focusId)) return true;
    }
  }
  return false;
};

const PreviewContent = ({
  contentData,
  showCheckboxes = false,
  selectedNodes = {},
  onNodeToggle,
  disabled = false,
  focusNodeId,
  auditSubstepHeading = null,
  auditStepOrdinalOverride = null,
  auditSourceSectionLabel = null,
  /** Matches audit version_content.preview_tree_node_id / procedure ref for section copy/move Review. */
  auditFocusSectionTreeNodeId = null,
  auditFocusStepTreeNodeId = null,
  auditOrdinalIndexFallback = false,
  auditOrdinalFallbackSectionNumber = 1,
  readOnlyCheckboxTitleBase,
  preferRelativeStepOrdinal = false,
  /** Audit `/audit-trail/review/...`: hide "Short Description" / "Rich text content" under the node heading */
  hideRichTextFieldLabels = false,
  breadcrumbText = null,
  procedureTreeForStepNumbering = null,
}) => {
  const readOnlyCheckboxTitleMatch =
    disabled &&
    readOnlyCheckboxTitleBase != null &&
    typeof readOnlyCheckboxTitleBase === "string"
      ? parseReadOnlyCheckboxTitleMatch(readOnlyCheckboxTitleBase)
      : null;

  if (!contentData || contentData.length === 0) {
    return null;
  }

  for (let i = 0; i < contentData.length; i++) {
    const section = contentData[i];
    if (section?.steps) {
      for (let j = 0; j < section.steps.length; j++) {
        const step = section.steps[j];
      }
    }
  }

  const filteredContentData = filterContentDataToFocusNode(
    contentData,
    focusNodeId,
  );
  const nodeIndexSourceData =
    Array.isArray(procedureTreeForStepNumbering) &&
    procedureTreeForStepNumbering.length > 0
      ? procedureTreeForStepNumbering
      : filteredContentData;
  const nodeIndices = getAllNodeIndices(nodeIndexSourceData);
  // Check if data has been pre-filtered for a single step (hideSectionHeader indicates this)
  const isPreFilteredForStep =
    filteredContentData.length === 1 &&
    filteredContentData[0]?.hideSectionHeader === true &&
    filteredContentData[0]?.steps?.length === 1;

  const fallbackSectionNum =
    typeof auditOrdinalFallbackSectionNumber === "number" &&
    !Number.isNaN(auditOrdinalFallbackSectionNumber) &&
    auditOrdinalFallbackSectionNumber >= 1
      ? auditOrdinalFallbackSectionNumber
      : 1;

  const auditSectionNumFromSourceLabel =
    parseAuditSectionNumberFromLabel(auditSourceSectionLabel);

  const auditFocusHitInTree = treeHasAuditStepFocusHit(
    filteredContentData,
    auditFocusStepTreeNodeId,
  );

  const useOrdinalIndexFallback =
    auditOrdinalIndexFallback === true &&
    auditStepOrdinalOverride != null &&
    !Number.isNaN(auditStepOrdinalOverride) &&
    auditFocusStepTreeNodeId &&
    !auditFocusHitInTree;

  const rowGetsAuditOverrides = (
    step,
    section,
    sectionIdx,
    stepIdx,
    data,
  ) => {
    const hasHeading =
      typeof auditSubstepHeading === "string" && auditSubstepHeading.trim();
    if (auditFocusStepTreeNodeId) {
      if (stepOrSubstepMatchesAuditFocus(step, auditFocusStepTreeNodeId))
        return true;
    } else if (hasHeading) {
      /* Substep heading only — do not treat step ordinal alone as matching every row */
      return true;
    }
    if (
      useOrdinalIndexFallback &&
      auditStepOrdinalOverride != null &&
      !Number.isNaN(auditStepOrdinalOverride)
    ) {
      const sectionOrd = getSectionDisplayOrdinal(
        section,
        sectionIdx,
        data,
      );
      if (
        sectionOrd === fallbackSectionNum &&
        stepIdx === auditStepOrdinalOverride - 1
      )
        return true;
    }
    return false;
  };

  let stepCount = 0;

  // If pre-filtered for a single step, render only that step without section wrapper
  if (isPreFilteredForStep) {
    const section = filteredContentData[0];
    const step = section.steps[0];

    const rowGets = rowGetsAuditOverrides(
      step,
      section,
      0,
      0,
      filteredContentData,
    );
    const preFilteredOrdinal =
      rowGets &&
      auditStepOrdinalOverride != null &&
      !Number.isNaN(auditStepOrdinalOverride)
        ? auditStepOrdinalOverride
        : null;
    const preFilteredSubstepHeading =
      rowGets &&
      typeof auditSubstepHeading === "string" &&
      auditSubstepHeading.trim()
        ? auditSubstepHeading.trim()
        : null;
    const preFilteredSectionLabel =
      preFilteredOrdinal != null || preFilteredSubstepHeading
        ? auditSourceSectionLabel
        : null;

    const treeSectionOrdinalForStep = getSectionDisplayOrdinal(
      section,
      0,
      filteredContentData,
    );
    const preFilteredSectionOrdinal =
      treeSectionOrdinalForStep != null &&
      !Number.isNaN(treeSectionOrdinalForStep)
        ? treeSectionOrdinalForStep
        : auditSectionNumFromSourceLabel != null &&
            !Number.isNaN(auditSectionNumFromSourceLabel)
          ? auditSectionNumFromSourceLabel
          : 1;

    const preFilteredProcedureWideStep =
      Array.isArray(procedureTreeForStepNumbering) &&
      procedureTreeForStepNumbering.length > 0
        ? getProcedureWideStepDisplayOrdinal(
            step,
            section,
            procedureTreeForStepNumbering,
          )
        : null;

    return (
      <Step
        key={"step_1"}
        step={step}
        stepCount={1}
        sectionIdx={0}
        sectionDisplayOrdinal={preFilteredSectionOrdinal}
        stepIdx={0}
        nodeIndices={nodeIndices}
        showCheckbox={showCheckboxes}
        isChecked={isStepSelected(step, selectedNodes)}
        onCheckboxChange={onNodeToggle}
        selectedNodes={selectedNodes}
        sectionIsSelected={isSectionSelected(section, selectedNodes)}
        disabled={disabled}
        readOnlyCheckboxTitleMatch={readOnlyCheckboxTitleMatch}
        procedureWideStepOrdinal={preFilteredProcedureWideStep}
        stepsInSection={[step]}
        auditSubstepHeading={preFilteredSubstepHeading}
        auditStepOrdinalOverride={preFilteredOrdinal}
        auditSourceSectionLabel={
          preFilteredSectionLabel ?? auditSourceSectionLabel
        }
        preferRelativeStepOrdinal={preferRelativeStepOrdinal}
        hideRichTextFieldLabels={hideRichTextFieldLabels}
      />
    );
  }

  // Otherwise, render the full tree structure
  return (
    <>
      {filteredContentData.map((section, sectionIdx) => {
        const sortedSteps = (section.steps || [])
          .slice()
          .sort((a, b) => {
            const orderA = parseInt(
              a.version_details?.version_content?.sibling_order ||
                a.sibling_order ||
                a.procedure_tree?.sibling_order ||
                "0",
              10,
            );
            const orderB = parseInt(
              b.version_details?.version_content?.sibling_order ||
                b.sibling_order ||
                b.procedure_tree?.sibling_order ||
                "0",
              10,
            );
            return orderA - orderB;
          });

        /**
         * Audit "Section N" describes the *source* of a copy/move, not list position. With two or
         * more top-level sections (e.g. original + paste), using it for chrome would label both
         * blocks "Section 1". Use sibling/list rank for headers whenever the tree shows multiple
         * sections; keep audit ordinal only for a single-section (narrowed) preview.
         */
        const sectionOrdinalForHeader =
          filteredContentData.length === 1 &&
          auditSectionNumFromSourceLabel != null &&
          !Number.isNaN(auditSectionNumFromSourceLabel)
            ? auditSectionNumFromSourceLabel
            : getSectionDisplayOrdinal(
                section,
                sectionIdx,
                filteredContentData,
              );

        return (
          <Section
            key={"section_" + sectionIdx}
            index={sectionIdx}
            section={section}
            sectionDisplayOrdinal={sectionOrdinalForHeader}
            showCheckbox={showCheckboxes}
            isChecked={isSectionSelected(section, selectedNodes)}
            onCheckboxChange={onNodeToggle}
            hideSectionHeader={section.hideSectionHeader}
            disabled={disabled}
            readOnlyCheckboxTitleMatch={readOnlyCheckboxTitleMatch}
            breadcrumbText={breadcrumbText}
          >
            {sortedSteps.map((step, stepIdx) => {
              stepCount++;
              const rowGets = rowGetsAuditOverrides(
                step,
                section,
                sectionIdx,
                stepIdx,
                filteredContentData,
              );
              const effectiveOrdinalOverride =
                rowGets &&
                auditStepOrdinalOverride != null &&
                !Number.isNaN(auditStepOrdinalOverride)
                  ? auditStepOrdinalOverride
                  : null;
              const effectiveSubstepHeading =
                rowGets &&
                typeof auditSubstepHeading === "string" &&
                auditSubstepHeading.trim()
                  ? auditSubstepHeading.trim()
                  : null;
              const effectiveSectionLabel =
                effectiveOrdinalOverride != null || effectiveSubstepHeading
                  ? auditSourceSectionLabel
                  : null;

              const procedureWideStepOrdinal =
                Array.isArray(procedureTreeForStepNumbering) &&
                procedureTreeForStepNumbering.length > 0
                  ? getProcedureWideStepDisplayOrdinal(
                      step,
                      section,
                      procedureTreeForStepNumbering,
                    )
                  : null;

              return (
                <Step
                  key={"step_" + stepCount}
                  step={step}
                  stepCount={stepCount}
                  sectionIdx={sectionIdx}
                  sectionDisplayOrdinal={sectionOrdinalForHeader}
                  stepIdx={stepIdx}
                  stepsInSection={sortedSteps}
                  nodeIndices={nodeIndices}
                  showCheckbox={showCheckboxes}
                  isChecked={isStepSelected(step, selectedNodes)}
                  onCheckboxChange={onNodeToggle}
                  selectedNodes={selectedNodes}
                  sectionIsSelected={isSectionSelected(section, selectedNodes)}
                  disabled={disabled}
                  readOnlyCheckboxTitleMatch={readOnlyCheckboxTitleMatch}
                  procedureWideStepOrdinal={procedureWideStepOrdinal}
                  auditSubstepHeading={effectiveSubstepHeading}
                  auditStepOrdinalOverride={effectiveOrdinalOverride}
                  auditSourceSectionLabel={
                    effectiveSectionLabel ?? auditSourceSectionLabel
                  }
                  preferRelativeStepOrdinal={preferRelativeStepOrdinal}
                  hideRichTextFieldLabels={hideRichTextFieldLabels}
                />
              );
            })}
          </Section>
        );
      })}
    </>
  );
};

export default PreviewContent;
